import cn from 'classnames';
import * as React from 'react';

import useInputValidation from '../use-input-validation';
import Text from '../text';

import { Checkbox as Props, themes } from './checkbox.types';

const RefCheckbox = React.forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<Props>
>(
  (
    {
      children,
      disabled,
      description,
      extra,
      intermediate,
      isLabelVisible = true,
      label,
      name,
      showCustomError = true,
      theme,
      value = '',
      isUnderlined,
      required,
      ...restProps
    },
    ref,
  ) => {
    const [onChange, onInvalid, error] = useInputValidation();

    return (
      <div
        className={cn('checkbox', {
          'checkbox--error': error,
          'checkbox--intermediate': intermediate,
          [`checkbox--${theme}`]: theme,
        })}
      >
        <label
          className={cn('checkbox__content', {
            'checkbox--underline': isUnderlined,
          })}
        >
          <div className="checkbox__main">
            <div className="checkbox__main-top">
              <div>
                <input
                  aria-label={isLabelVisible ? undefined : label}
                  className="checkbox__input"
                  type="checkbox"
                  name={name}
                  onChange={e => {
                    // NOTE: If props specify an `onChange`, we need to call that too
                    if (restProps.onChange) restProps.onChange(e);
                    onChange(e);
                  }}
                  onInvalid={e => {
                    // NOTE: If props specify an `onInvalid`, we need to call that too
                    if (restProps.onInvalid) restProps.onInvalid(e);
                    onInvalid(e);
                  }}
                  ref={ref}
                  value={value}
                  disabled={disabled}
                  required={required}
                  {...restProps}
                />

                <div
                  className={cn('checkbox__icon', {
                    'checkbox__icon--intermediate': intermediate,
                    [`checkbox__icon--${theme}`]: theme,
                  })}
                >
                  <svg
                    className="checkbox__intermediate"
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line
                      x1="0"
                      y1="5"
                      x2="10"
                      y2="5"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  <svg
                    className="checkbox__check"
                    width="12"
                    height="10"
                    viewBox="0 0 12 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.76123 9.0605L0.700729 5L1.76123 3.9395L4.76123 6.9395L10.7612 0.939501L11.8217 2L4.76123 9.0605Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
              {isLabelVisible ? (
                <>
                  {label ? (
                    <Text
                      className={cn('checkbox__label', {
                        [`checkbox__label--${theme}`]: theme,
                        'checkbox__label--disabled': disabled,
                      })}
                      size={Text.sizes.basic}
                    >
                      {label}
                    </Text>
                  ) : (
                    <div
                      className={cn('checkbox__label', {
                        [`checkbox__label--${theme}`]: theme,
                        'checkbox__label--disabled': disabled,
                      })}
                    >
                      {children}
                    </div>
                  )}
                </>
              ) : null}
              {required ? <span className="checkbox__asterisk">*</span> : null}
            </div>
            {description && (
              <Text
                className={cn('checkbox__description', {
                  [`checkbox__label--${theme}`]: theme,
                  'checkbox__label--disabled': disabled,
                })}
                size={Text.sizes.small}
              >
                {description}
              </Text>
            )}
          </div>
        </label>

        {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
        {error && showCustomError && (
          <div
            aria-hidden="true"
            className={cn('checkbox__error', {
              [`checkbox__error--${theme}`]: theme,
            })}
          >
            {error}
          </div>
        )}

        {extra && <span className="checkbox__extra">{extra}</span>}
      </div>
    );
  },
);

// NOTE: If this isn't set, stack traces say "forwardRef" instead of "Checkbox"
RefCheckbox.displayName = 'Checkbox';

// NOTE: The component is redeclared here because TypeScript doesn't accept assigning extra properties to `RefComponent` when it's declared
const Checkbox: typeof RefCheckbox & {
  themes: typeof themes;
} = Object.assign(RefCheckbox, { themes });

export default Checkbox;
